/* ════ WHILE YOU WERE AWAY ═════════════════════════════════ */
// On startup, a short summary of what other people did since you last had
// GRANTT open: tasks added or changed on your lane, new cards in your Inbox,
// cards you've been flagged on, and leave news. "Last seen" is remembered per
// person on each browser, refreshed while GRANTT is open and when you leave.
// Nothing shows on your first visit, or when there's nothing new.
import { state } from './state.js';
import { $ } from './dom.js';
import { MO } from './config.js';
import { setView, focusTask } from './views.js';
import { openIdeaEdit } from './modals/idea.js';
import { displayName } from './utils/name.js';

const lc = s => String(s || '').toLowerCase();
const me = () => lc(state.currentUser?.email);
const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const seenKey = () => `grantt_last_seen_${me()}`;
const nameOf = email => state.resources.find(r => lc(r.user_email) === lc(email))?.name || displayName(email || '') || 'Someone';
const after = (ts, since) => !!ts && new Date(ts) > since;
const byOther = who => !!who && lc(who) !== me();

let lastDigest = null;

function readSeen() { try { const v = localStorage.getItem(seenKey()); return v ? new Date(v) : null; } catch (e) { return null; } }
function markSeen() { if (!me()) return; try { localStorage.setItem(seenKey(), new Date().toISOString()); } catch (e) { /* private mode */ } }

function when(since) {
  const d = new Date(since), today = new Date(); today.setHours(0, 0, 0, 0);
  const t = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (d >= today) return `today at ${t}`;
  const y = new Date(today); y.setDate(y.getDate() - 1);
  if (d >= y) return `yesterday at ${t}`;
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()]} ${d.getDate()} ${MO[d.getMonth()]} at ${t}`;
}
const dshort = iso => { if (!iso) return ''; const [y, m, d] = iso.split('-').map(Number); return `${d} ${MO[m - 1]}`; };

// Everything other people did that concerns me since `since`.
export function buildDigest(since) {
  const mine = new Set(state.resources.filter(r => lc(r.user_email) === me()).map(r => r.id));
  const sections = [];

  const added = state.tasks.filter(t => mine.has(t.resource) && after(t.created_at, since) && byOther(t.created_by));
  const addedIds = new Set(added.map(t => t.id));
  const changed = state.tasks.filter(t => mine.has(t.resource) && !addedIds.has(t.id)
    && after(t.updated_at, since) && byOther(t.updated_by));
  if (added.length) sections.push({ key: 'added', icon: '📌', title: 'Added to your lane', items: added.map(t => ({
    kind: 'task', id: t.id, text: t.name || 'Untitled task', sub: `${dshort(t.start)}${t.end && t.end !== t.start ? ` – ${dshort(t.end)}` : ''} · by ${nameOf(t.created_by)}` })) });
  if (changed.length) sections.push({ key: 'changed', icon: '✏️', title: 'Changed on your lane', items: changed.map(t => ({
    kind: 'task', id: t.id, text: t.name || 'Untitled task', sub: `${dshort(t.start)}${t.end && t.end !== t.start ? ` – ${dshort(t.end)}` : ''} · by ${nameOf(t.updated_by)}` })) });

  const flagged = state.ideas.filter(i => (i.mentions || []).map(lc).includes(me())
    && ((after(i.created_at, since) && byOther(i.created_by)) || (after(i.updated_at, since) && byOther(i.updated_by))));
  const flaggedIds = new Set(flagged.map(i => i.id));
  const inbox = state.ideas.filter(i => mine.has(i.resource) && !flaggedIds.has(i.id) && after(i.created_at, since) && byOther(i.created_by));
  if (flagged.length) sections.push({ key: 'flagged', icon: '🔔', title: 'Flagged for you', items: flagged.map(i => ({
    kind: 'idea', id: i.id, text: i.name || 'Untitled card', sub: `by ${nameOf(after(i.updated_at, since) ? i.updated_by || i.created_by : i.created_by)}` })) });
  if (inbox.length) sections.push({ key: 'inbox', icon: '📥', title: 'New in your Inbox', items: inbox.map(i => ({
    kind: 'idea', id: i.id, text: i.name || 'Untitled card', sub: `from ${nameOf(i.created_by)}` })) });

  const leave = [];
  (state.leaveRequests || []).forEach(r => {
    if (lc(r.user_email) === me() && ['approved', 'denied'].includes(r.status) && after(r.decided_at, since) && byOther(r.decided_by))
      leave.push({ kind: 'myleave', text: `Your leave ${dshort(r.start)}${r.end !== r.start ? ` – ${dshort(r.end)}` : ''} was ${r.status === 'approved' ? 'approved ✅' : 'declined'}`,
        sub: r.status === 'denied' && r.decision_note ? `Reason: ${r.decision_note}` : `by ${nameOf(r.decided_by)}` });
  });
  const approver = lc(state.settings.leave_approver) === me();
  if (approver) (state.leaveRequests || []).forEach(r => {
    if (r.status === 'pending' && lc(r.user_email) !== me() && after(r.created_at, since))
      leave.push({ kind: 'review', text: `${nameOf(r.user_email)} asked for leave ${dshort(r.start)}${r.end !== r.start ? ` – ${dshort(r.end)}` : ''}`, sub: 'Waiting for you to review' });
  });
  if (leave.length) sections.push({ key: 'leave', icon: '🌴', title: 'Leave', items: leave });

  return sections;
}

function render(d) {
  const n = d.sections.reduce((k, s) => k + s.items.length, 0);
  $('dg-sub').textContent = `${n} ${n === 1 ? 'thing' : 'things'} since you were last here, ${when(d.since)}.`;
  $('dg-body').innerHTML = d.sections.map(s => `<div class="dg-sec">
      <div class="dg-h"><span>${s.icon}</span>${esc(s.title)}<b>${s.items.length}</b></div>
      ${s.items.slice(0, 8).map((it, k) => `<button class="dg-item" data-dg="${s.key}:${k}">
        <span class="dg-t">${esc(it.text)}</span><span class="dg-s">${esc(it.sub || '')}</span><span class="dg-go">Open →</span></button>`).join('')}
      ${s.items.length > 8 ? `<div class="dg-more">and ${s.items.length - 8} more</div>` : ''}</div>`).join('')
    || '<div class="dg-empty">Nothing new since you were last here.</div>';
}

function open(d) { render(d); $('digest-modal').classList.add('open'); }
function close() { $('digest-modal').classList.remove('open'); }

function go(key) {
  const [sk, k] = key.split(':'), it = lastDigest?.sections.find(s => s.key === sk)?.items[+k];
  if (!it) return;
  close();
  if (it.kind === 'task') focusTask(it.id);
  else if (it.kind === 'idea') { setView('ideas'); setTimeout(() => openIdeaEdit(it.id), 60); }
  else if (it.kind === 'myleave') $('btn-leave-mine')?.click();
  else if (it.kind === 'review') setView('leave');
}

// Called once the data has loaded. Waits for the What's new popup to close first.
export function showDigestOnStartup() {
  if (!me()) return;
  const since = readSeen();
  markSeen();
  if (!since) return;   // first visit on this browser: nothing to compare against
  const sections = buildDigest(since);
  lastDigest = { since, sections };
  const b = $('btn-digest'); if (b) b.hidden = !sections.length;
  if (!sections.length) return;
  const wn = $('whatsnew-modal');
  const tryOpen = () => { if (wn?.classList.contains('open')) return setTimeout(tryOpen, 500); open(lastDigest); };
  setTimeout(tryOpen, 900);
}

export function initDigest() {
  $('dg-ok')?.addEventListener('click', close);
  $('dg-x')?.addEventListener('click', close);
  $('dg-body')?.addEventListener('click', e => { const b = e.target.closest('[data-dg]'); if (b) go(b.dataset.dg); });
  $('btn-digest')?.addEventListener('click', () => { if (lastDigest) open(lastDigest); });
  // Keep "last seen" fresh while GRANTT is open, and when you leave.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') markSeen(); });
  window.addEventListener('pagehide', markSeen);
  setInterval(() => { if (document.visibilityState === 'visible') markSeen(); }, 60000);
}
